import { useMemo } from "react";

import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import { Alert, Box, CircularProgress, Stack, ToggleButton, Typography } from "@mui/material";
import { formatShortWeekdayDate, formatTime } from "@shared";
import { startOfDay } from "date-fns";

import { ExchangeableAppointmentResponse } from "@/api";
import { AppointmentPurposeChip } from "@/common/components/appointments/shared/components/appointment-purpose-chip/AppointmentPurposeChip";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { AppointmentLabels, useAppointmentLabels } from "@/common/hooks/useAppointmentLabels";
import { useTranslation } from "@/translations";

interface Props {
  labelledById: string;
  datesSelected: boolean;
  rangeValid: boolean;
  appointments: ExchangeableAppointmentResponse[];
  isLoading: boolean;
  isError: boolean;
  selectedAppointmentId: string | null;
  onToggle: (appointmentId: string) => void;
  /** Marks the panel after a submit attempt with no Termin selected. */
  hasError?: boolean;
  /** Id of the section's error text, so the group announces why it is marked. */
  describedById?: string;
  /** Replaces the default "no Termine in this range" text, e.g. when a filter hides them. */
  emptyText?: string;
}

interface DayGroup {
  key: number;
  label: string;
  appointments: ExchangeableAppointmentResponse[];
}

/** The own Termine of the date range, grouped by day; exactly one can be selected as the offered Dienst. */
export function ExchangeableAppointmentPicker({
  labelledById,
  datesSelected,
  rangeValid,
  appointments,
  isLoading,
  isError,
  selectedAppointmentId,
  onToggle,
  hasError = false,
  describedById,
  emptyText,
}: Props) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const labels = useAppointmentLabels();

  // The backend returns the Termine chronologically; grouping by day is display-only.
  const dayGroups = useMemo(() => {
    const groups: DayGroup[] = [];
    for (const appointment of appointments) {
      const day = startOfDay(appointment.startTimeUtc).getTime();
      const lastGroup = groups[groups.length - 1];
      if (lastGroup?.key === day) {
        lastGroup.appointments.push(appointment);
      } else {
        groups.push({
          key: day,
          label: formatShortWeekdayDate(appointment.startTimeUtc, locales[language]),
          appointments: [appointment],
        });
      }
    }
    return groups;
  }, [appointments, language]);

  const hint = !datesSelected
    ? t("exchange.picker.selectDatesHint")
    : !rangeValid
      ? t("exchange.validation.tillBeforeFrom")
      : null;

  return (
    <Box
      sx={{
        backgroundColor: "action.hover",
        borderRadius: 1,
        p: 2,
        height: 300,
        overflowY: "auto",
        border: 1,
        borderColor: hasError ? "error.main" : "transparent",
      }}
    >
      {hint !== null ? (
        <CenteredHint>
          <EventAvailableIcon color="disabled" fontSize="large" />
          <Typography color="text.secondary" sx={{ textAlign: "center" }}>
            {hint}
          </Typography>
        </CenteredHint>
      ) : isLoading ? (
        <CenteredHint>
          <CircularProgress aria-label={t("exchange.picker.loading")} />
        </CenteredHint>
      ) : isError ? (
        <CenteredHint>
          <Alert severity="error" sx={{ width: "100%" }}>
            {t("exchange.picker.loadError")}
          </Alert>
        </CenteredHint>
      ) : dayGroups.length === 0 ? (
        <CenteredHint>
          <Typography color="text.secondary" sx={{ textAlign: "center" }}>
            {emptyText ?? t("exchange.picker.empty")}
          </Typography>
        </CenteredHint>
      ) : (
        <Stack
          spacing={2}
          role="group"
          aria-labelledby={labelledById}
          aria-describedby={hasError ? describedById : undefined}
        >
          {dayGroups.map((group) => (
            <Box key={group.key}>
              <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
                {group.label}
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
                {group.appointments.map((appointment) => (
                  <AppointmentCard
                    key={appointment.appointmentId}
                    appointment={appointment}
                    selected={selectedAppointmentId === appointment.appointmentId}
                    onToggle={() => onToggle(appointment.appointmentId)}
                    labels={labels}
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}

function CenteredHint({ children }: { children: React.ReactNode }) {
  return (
    <Stack spacing={1} sx={{ height: "100%", alignItems: "center", justifyContent: "center" }}>
      {children}
    </Stack>
  );
}

interface AppointmentCardProps {
  appointment: ExchangeableAppointmentResponse;
  selected: boolean;
  onToggle: () => void;
  labels: AppointmentLabels;
}

function AppointmentCard({ appointment, selected, onToggle, labels }: AppointmentCardProps) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();

  const time = appointment.isAllDay ? t("exchange.allDay") : formatTime(appointment.startTimeUtc, locales[language]);
  const location = appointment.location ?? null;

  return (
    <ToggleButton
      value={appointment.appointmentId}
      selected={selected}
      onChange={onToggle}
      sx={{
        position: "relative",
        flexDirection: "column",
        alignItems: "flex-start",
        textAlign: "left",
        backgroundColor: "background.paper",
        minWidth: 220,
        pl: 2,
        pr: 5,
        py: 1.5,
        gap: 0.5,
        // The theme's MuiToggleButton variant declares hover and selected+focusVisible with higher
        // specificity than a bare sx rule, so both states have to be restated here or the card
        // repaints itself to the panel's grey on hover and on keyboard focus.
        "&:hover": { backgroundColor: "background.paper" },
        "&.Mui-selected, &.Mui-selected:hover": {
          backgroundColor: "background.paper",
          color: "text.primary",
          borderColor: "primary.main",
          boxShadow: (theme) => `inset 0 0 0 1px ${theme.palette.primary.main}`,
          "&.Mui-focusVisible": {
            backgroundColor: "background.paper",
            color: "text.primary",
          },
        },
      }}
    >
      {selected && (
        <CheckCircleRoundedIcon color="primary" fontSize="small" sx={{ position: "absolute", top: 8, right: 8 }} />
      )}
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="body2" fontWeight={700}>
          {time}
        </Typography>
        <AppointmentPurposeChip label={labels.purposeLabelOf(appointment.purposeKey)} />
      </Box>
      {location && (
        <Box display="flex" alignItems="center" gap={0.5}>
          <PlaceRoundedIcon color="action" sx={{ fontSize: 16 }} />
          <Typography variant="caption" color="text.secondary">
            {location}
          </Typography>
        </Box>
      )}
      {appointment.details && (
        <Typography variant="caption" color="text.secondary">
          {appointment.details}
        </Typography>
      )}
    </ToggleButton>
  );
}
